import Toast from "components/cms/Toast";
import SectionCard from "components/cms/SectionCard";
import {
  AreaInput,
  CardRow,
  FieldRow,
  FileInput,
  GroupBox,
  TextInput,
} from "components/cms/fields";
import useCmsPage from "hooks/useCmsPage";
import { addBasic, getBasic, updateBasic } from "api/basic";
import { emptyBasic } from "utils/defaults";

const FOOTER_KEYS = [
  "footerLogo",
  "footerAddresses",
  "footerFollowTitle",
  "socialMediaIcon",
  "footerColumns",
  "footerCertificationText",
  "footerCertificationLogos",
  "footerCopyright",
  "footerLegalLinks",
];

export default function FooterPage() {
  const {
    form,
    setField,
    frMap,
    loading,
    toast,
    setToast,
    saveSection,
    savingSection,
    isSectionDirty,
  } = useCmsPage({
    load: getBasic,
    create: addBasic,
    update: updateBasic,
    empty: emptyBasic,
    mapResponse: (res) => res.data?.data || res.data?.info,
    prepare: (payload) => ({
      ...payload,
      socialMediaIcon: (payload.socialMediaIcon || []).map((item) => ({
        link: item?.link || "",
      })),
    }),
  });

  if (loading) {
    return <p className="mt-5 text-gray-600">Loading Footer…</p>;
  }

  return (
    <div className="mt-3">
      <Toast toast={toast} onClose={() => setToast(null)} />

      <SectionCard
        title="Footer"
        description="Logo, offices, links, certification, and legal"
        onSave={() => saveSection("footer", FOOTER_KEYS)}
        saving={savingSection === "footer"}
        canSave={isSectionDirty(FOOTER_KEYS)}
      >
        <FileInput
          label="Footer logo"
          path="footerLogo"
          form={form}
          setField={setField}
        />
        <FieldRow>
          <TextInput
            label="Follow us title"
            path="footerFollowTitle"
            form={form}
            setField={setField}
            frMap={frMap}
          />
        </FieldRow>

        <CardRow>
          {(form.footerAddresses || []).map((_, index) => (
            <GroupBox key={`address-${index}`} title={`Address ${index + 1}`}>
              <TextInput
                label="City label"
                path={`footerAddresses.${index}.label`}
                form={form}
                setField={setField}
                frMap={frMap}
              />
              <TextInput
                label="City"
                path={`footerAddresses.${index}.city`}
                form={form}
                setField={setField}
                frMap={frMap}
              />
              <TextInput
                label="Street"
                path={`footerAddresses.${index}.street`}
                form={form}
                setField={setField}
                frMap={frMap}
              />
              <TextInput
                label="Country"
                path={`footerAddresses.${index}.country`}
                form={form}
                setField={setField}
                frMap={frMap}
              />
              <TextInput
                label="Phone number"
                path={`footerAddresses.${index}.phoneNumber`}
                form={form}
                setField={setField}
              />
            </GroupBox>
          ))}
        </CardRow>

        <GroupBox title="Social links">
          <FieldRow>
            {(form.socialMediaIcon || []).map((_, index) => (
              <TextInput
                key={`social-${index}`}
                label={`Social link ${index + 1}`}
                path={`socialMediaIcon.${index}.link`}
                form={form}
                setField={setField}
              />
            ))}
          </FieldRow>
        </GroupBox>

        <CardRow>
          {(form.footerColumns || []).map((column, index) => (
            <GroupBox key={`column-${index}`} title={`Column ${index + 1}`}>
              <TextInput
                label="Column title"
                path={`footerColumns.${index}.title`}
                form={form}
                setField={setField}
                frMap={frMap}
              />
              <CardRow>
                {(column.links || []).map((_, linkIndex) => (
                  <GroupBox
                    key={`column-${index}-link-${linkIndex}`}
                    title={`Link ${linkIndex + 1}`}
                    plain
                  >
                    <TextInput
                      label="Title"
                      path={`footerColumns.${index}.links.${linkIndex}.title`}
                      form={form}
                      setField={setField}
                      frMap={frMap}
                    />
                    <TextInput
                      label="Link"
                      path={`footerColumns.${index}.links.${linkIndex}.link`}
                      form={form}
                      setField={setField}
                    />
                  </GroupBox>
                ))}
              </CardRow>
            </GroupBox>
          ))}
        </CardRow>

        <AreaInput
          label="Certification text"
          path="footerCertificationText"
          form={form}
          setField={setField}
          frMap={frMap}
        />

        <GroupBox title="Certification logos" cols="sm:grid-cols-2 lg:grid-cols-3">
          {(form.footerCertificationLogos || []).map((_, index) => (
            <FileInput
              key={`cert-logo-${index}`}
              label={`Logo ${index + 1}`}
              path={`footerCertificationLogos.${index}`}
              form={form}
              setField={setField}
              extra=""
            />
          ))}
        </GroupBox>

        <FieldRow>
          <TextInput
            label="Copyright"
            path="footerCopyright"
            form={form}
            setField={setField}
            frMap={frMap}
          />
        </FieldRow>

        <CardRow>
          {(form.footerLegalLinks || []).map((_, index) => (
            <GroupBox key={`legal-${index}`} title={`Legal link ${index + 1}`}>
              <TextInput
                label="Title"
                path={`footerLegalLinks.${index}.title`}
                form={form}
                setField={setField}
                frMap={frMap}
              />
              <TextInput
                label="Link"
                path={`footerLegalLinks.${index}.link`}
                form={form}
                setField={setField}
              />
            </GroupBox>
          ))}
        </CardRow>
      </SectionCard>
    </div>
  );
}
